import React, { useCallback } from "react";
import styles from "./Pill.module.scss";

/**
 * `Pill` is a component that displays a clickable pill with an icon and text.
 *
 * @component
 * @param {Object} props - The properties that define the component.
 * @param {Function} props.onClick - The function to be called when the pill is clicked.
 * @param {React.ReactNode} props.icon - The icon to be displayed in the pill.
 * @param {string} props.text - The text to be displayed in the pill.
 * @param {string} props.iconPosition - The position of the icon in the pill. Can be either 'before' or 'after'.
 * @param {boolean} props.selected - A flag that indicates whether the pill is selected.
 *
 * @returns {ReactElement} The `Pill` component.
 */

export enum PILL_ICON_POSITION {
  BEFORE = "before",
  AFTER = "after",
}

interface PillProps {
  onClick: (trackEventObj: TrackEventObj) => void;
  icon: React.ReactNode;
  text: string;
  iconPosition: PILL_ICON_POSITION;
  selected: boolean;
  count?: number;
}

// non-breaking space unicode
const NBSP = "\u00A0";

export const Pill: React.FC<PillProps> = React.memo((props) => {
  const { onClick, selected, icon, text, count = 0, iconPosition } = props;
  const textWithCount = count > 0 ? `${text}${NBSP}(${count})` : text;

  const handleButtonClick = useCallback(() => {
    onClick({
      text,
    });
  }, [onClick, text]);

  return (
    <button
      className={`lll-text-body-3 OneLinkTx ${styles.pillBttn} ${
        selected ? styles.pillBttn_selected : ""
      }`}
      onClick={handleButtonClick}
    >
      {iconPosition === PILL_ICON_POSITION.BEFORE && (
        <div className={styles.pillBttn_content}>
          <div
            className={`${styles.pillBttn_icon} ${styles.pillBttn_rightMargin}`}
          >
            {icon}
          </div>
          <span>{textWithCount}</span>
        </div>
      )}
      {iconPosition === PILL_ICON_POSITION.AFTER && (
        <div className={styles.pillBttn_content}>
          <span className={styles.pillBttn_rightMargin}>{textWithCount}</span>
          <span className={styles.pillBttn_icon}>{icon}</span>
        </div>
      )}
    </button>
  );
});

Pill.displayName = "Pill";
